import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import Button from '../../library/button/button';
import Gauge from '../../library/gauge/gauge';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import { calculateLifeExpectancy } from '../tire-life-cost-info/tire-life-cost-info-utils';
import TreadLifeEstimate from '../tread-life-estimate/tread-life-estimate';

import { useFitment, useTreadwellPreferences } from '../../shared/hooks/hooks';

import {
  CmsPromotionBannerComponent,
  CmsToolTips,
  CmsToolTipsComponent,
  FuelTypeEnum,
  ProductData,
  TreadWellPriorityEnum
} from '../../shared/types/graphql-types';
import { TIRE_LIFE_TOOLTIPS } from '../tire-life-cost-info/constants/tire-life-cost-constants';

import './product-first-priority-details.scss';

interface PriorityDetails {
  gaugeColor1?: string;
  gaugeColor2?: string;
  gaugeDescription1?: ReactNode;
  gaugeDescription2?: ReactNode;
  gaugeValue1?: number;
  gaugeValue2?: number;
  id: TreadWellPriorityEnum;
  noGaugeDescription1?: ReactNode;
  noGaugeDescription2?: ReactNode;
  title: string;
}

interface ProductFirstPriorityDetailsProps {
  dataReportUrl?: string;
  hideLink?: boolean;
  medianMileage?: number;
  minMileage?: number;
  onClick?: () => void;
  product: ProductData;
}

function ProductFirstPriorityDetails(props: ProductFirstPriorityDetailsProps) {
  const {
    dataReportUrl = 'https://www.discounttire.com/404',
    hideLink = false,
    medianMileage,
    minMileage,
    onClick,
    product
  } = props;

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { selectedFitment } = useFitment();
  const isEV = selectedFitment?.fuelType === FuelTypeEnum.Bev;

  const {
    dryDistance,
    dryRating,
    rollingResistanceRating,
    wetDistance,
    wetWornDistance,
    wetRating
  } = product?.recommendationData?.stopping || {};
  const { comfort, handling, quiet, wetHandling } =
    product?.recommendationData?.rideRating || {};
  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    15 * 1000
  );
  const { milesPerDollar } = product?.recommendationData || {};

  const priorityDetailsArr: PriorityDetails[] = useMemo(
    () => [
      {
        gaugeColor1: '#3483DE',
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            styleName="no-wrap"
            tag="strong"
          >
            <div styleName="wet-roads">Wet Roads</div>
            <span styleName="new-tread">
              New tread: <b>{Math.floor(wetDistance || 0)} ft.</b>
            </span>
            <span>
              Worn tread: <b>{Math.floor(wetWornDistance || 0)} ft.</b>
            </span>
          </Typography>
        ),
        gaugeDescription2: (
          <>
            <Typography
              className="product-first-priority-details__description"
              size="small"
              tag="strong"
            >
              Dry Roads
            </Typography>
            <Typography
              className="product-first-priority-details__description"
              size="small"
              tag="span"
              weight="black"
            >
              {Math.floor(dryDistance || 0)} ft.
            </Typography>
          </>
        ),
        gaugeValue1: wetRating || 0,
        gaugeValue2: dryRating || 0,
        id: TreadWellPriorityEnum.StoppingDistance,
        title: 'Stopping Distance at 60 mph'
      },
      {
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Quiet Ride
          </Typography>
        ),
        gaugeDescription2: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Comfort
          </Typography>
        ),
        gaugeValue1: quiet || 0,
        gaugeValue2: comfort || 0,
        id: TreadWellPriorityEnum.ComfortAndNoise,
        title: 'Ride Quality'
      },
      {
        gaugeColor1: '#52A240',
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            styleName="no-wrap"
            tag="strong"
          >
            {isEV ? 'EV Range' : 'Fuel Efficiency'}{' '}
            <Tooltip
              content={
                'Our testing shows that a tire rated at 1 provides 4% lower fuel economy than a tire rated at 5.'
              }
            />
          </Typography>
        ),
        gaugeValue1: rollingResistanceRating || 0,
        id: TreadWellPriorityEnum.FuelEfficiency,
        title: 'Efficiency'
      },
      {
        gaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Steering Response
          </Typography>
        ),
        gaugeDescription2: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            Wet Handling Response
          </Typography>
        ),
        gaugeValue1: handling || 0,
        gaugeValue2: wetHandling || 0,
        id: TreadWellPriorityEnum.Handling,
        title: 'Handling'
      },
      {
        id: TreadWellPriorityEnum.LifeOfTire,
        noGaugeDescription1: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            <div styleName="counter-time-wrapper">
              <div styleName="counter-value">{tireLifeYears}</div>
              <div styleName="counter-label--years">years</div>
              <div styleName="counter-value">{tireLifeMonths}</div>
              <div styleName="counter-label">months</div>
              <Tooltip
                content={TIRE_LIFE_TOOLTIPS.tireLife}
                styleName="tooltip"
              />
            </div>
          </Typography>
        ),
        noGaugeDescription2: (
          <Typography
            className="product-first-priority-details__description"
            size="small"
            tag="strong"
          >
            {milesPerDollar && (
              <div styleName="counter-miles-wrapper">
                <div styleName="counter-value">{milesPerDollar}</div>
                <div styleName="counter-label">
                  <div>miles per</div>
                  <div>dollar</div>
                </div>
                <Tooltip
                  content={TIRE_LIFE_TOOLTIPS.milesPerDollar}
                  styleName="tooltip"
                />
              </div>
            )}
          </Typography>
        ),
        title: 'Tire Life & Cost'
      }
    ],
    [
      comfort,
      dryDistance,
      dryRating,
      handling,
      isEV,
      milesPerDollar,
      quiet,
      rollingResistanceRating,
      tireLifeMonths,
      tireLifeYears,
      wetDistance,
      wetHandling,
      wetRating,
      wetWornDistance
    ]
  );
  const [detailsObj, setDetailsObj] = useState<PriorityDetails>(
    priorityDetailsArr[0]
  );

  useEffect(() => {
    function getNumber1PriorityId() {
      let priorityId = TreadWellPriorityEnum.StoppingDistance;

      if (treadwellPreferences) {
        if (treadwellPreferences.isCustomDriverTypeShowing) {
          const customDriverType =
            treadwellPreferences.selectedCustomDrivingPriorities as CmsPromotionBannerComponent[];
          priorityId = customDriverType[0].id as TreadWellPriorityEnum;
        } else if (treadwellPreferences.selectedDriverType) {
          const driverType =
            treadwellPreferences.selectedDriverType as CmsToolTipsComponent;
          const tooltips = driverType.tooltips as CmsToolTips[];
          priorityId = tooltips[0].id as TreadWellPriorityEnum;
        }
      }

      return priorityId;
    }

    const detailsObj = priorityDetailsArr.filter(
      item => item.id === getNumber1PriorityId()
    )[0];

    setDetailsObj(detailsObj);
  }, [priorityDetailsArr, treadwellPreferences]);

  return (
    <div className="product-first-priority-details" styleName="container">
      <div styleName="header">
        <Typography
          className="product-first-priority-details__header"
          styleName="title"
          tag="b"
          weight="black"
        >
          {detailsObj.title}
        </Typography>
      </div>
      <div styleName="footer">
        {detailsObj.gaugeValue1 && (
          <div styleName="gauge-container">
            {isTreadwellFlow && (
              <Gauge
                draw
                fillPerc={detailsObj.gaugeValue1 / 5}
                gaugeColor={detailsObj.gaugeColor1 || undefined}
                gaugeHeight={38.5}
                gaugeStrokeWidth={20}
                styleName="gauge"
                value={detailsObj.gaugeValue1}
              />
            )}
            <span>{detailsObj.gaugeDescription1}</span>
          </div>
        )}
        {detailsObj.gaugeValue2 && (
          <div styleName="gauge-container">
            {isTreadwellFlow && (
              <Gauge
                draw
                fillPerc={detailsObj.gaugeValue2 / 5}
                gaugeColor={detailsObj.gaugeColor2 || undefined}
                gaugeHeight={38.5}
                gaugeStrokeWidth={20}
                styleName="gauge"
                value={detailsObj.gaugeValue2}
              />
            )}
            <span>{detailsObj.gaugeDescription2}</span>
          </div>
        )}
        {detailsObj.noGaugeDescription1 && (
          <div styleName="gauge-container">
            <span>{detailsObj.noGaugeDescription1}</span>
          </div>
        )}
        {detailsObj.noGaugeDescription2 && (
          <div styleName="gauge-container">
            <span>{detailsObj.noGaugeDescription2}</span>
          </div>
        )}
      </div>

      {medianMileage && isTreadwellFlow && (
        <TreadLifeEstimate
          medianMileage={medianMileage}
          minMileage={minMileage}
          styleName="tread-life"
        />
      )}

      {!hideLink && (
        <Button
          href={`${dataReportUrl}#treadwell-data`}
          onClick={onClick}
          size="regular"
          styleName="button"
        >
          see full data report
        </Button>
      )}
    </div>
  );
}

export default ProductFirstPriorityDetails;
